import React, { useRef } from 'react'

import betOptionButton from '@assets/side-button-2.svg?inline'
import betOptionButtonEQ from '@assets/jackpot-eq/side-button-2.svg?inline'

import { useConfigurationStore, useSpinButtonSectionStore, useJackpotStore } from '@stores'

import closeButton from '@assets/close-button.svg?inline'
import useOpacityToggle from '@hooks/useOpacityToggle'
import { SideButton } from '../'
import { BetOptions } from '../../'
import styles from '../SpinButtonSection.module.css'
import {
    BetChooserStartsClosingMessage,
    BetChooserStartsOpeningMessage,
    ChangeOpacityComponents,
    GameUIMessageType,
    OpenBetChooserClickedMessage,
} from 'game-ui-types'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useTranslation } from '@amusnet/translations/react'
import { cn } from '@amusnet/react-ui'

interface BetOptionButtonProps {
    isActive: boolean
    isInteractive: boolean
    shouldUpdateOpacityWhenInactive?: boolean
    pulse?: boolean
}

const BetOptionButton: React.FC<BetOptionButtonProps> = ({
    isActive,
    isInteractive,
    shouldUpdateOpacityWhenInactive,
    pulse,
}) => {
    const { t, isLoading } = useTranslation()

    const buttonConfigStyles = useConfigurationStore((state) => state.getComponentConfig('BetOptionButton').styles)
    const sectionConfigStyles = useConfigurationStore((state) => state.getComponentConfig('BetOptionSection').styles)
    const sectionConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('BetOptionSection').position
    )

    const isBetSectionOpened = useSpinButtonSectionStore((state) => state.isBetSectionOpened)
    const toggleBetSection = useSpinButtonSectionStore((state) => state.toggleBetSection)
    const activeTriggerButtonLabel = useSpinButtonSectionStore((state) => state.activeTriggerButtonLabel)
    const setActiveTriggerButtonLabel = useSpinButtonSectionStore((state) => state.setActiveTriggerButtonLabel)

    const isEQJackpotType = useJackpotStore((state) => state.isEQJackpotType)

    const betOptionButtonComponent = useRef<HTMLDivElement | null>(null)
    const betOptionModalRef = useRef<HTMLDivElement | null>(null)

    useOpacityToggle(ChangeOpacityComponents.BET_OPTION_BUTTON, betOptionButtonComponent)

    const onSideButtonClicked = () => {
        const openChooserClickedMessage: OpenBetChooserClickedMessage = {
            type: GameUIMessageType.OPEN_BET_CHOOSER_CLICKED,
        }

        window.postMessage(openChooserClickedMessage, '*')

        setActiveTriggerButtonLabel('betSection')

        if (isBetSectionOpened) {
            const chooserStartsClosingMessage: BetChooserStartsClosingMessage = {
                type: GameUIMessageType.BET_CHOOSER_STARTS_CLOSING,
            }

            window.postMessage(chooserStartsClosingMessage, '*')
        } else {
            const chooserStartsOpeningMessage: BetChooserStartsOpeningMessage = {
                type: GameUIMessageType.BET_CHOOSER_STARTS_OPENING,
            }

            window.postMessage(chooserStartsOpeningMessage, '*')
        }

        toggleBetSection()
    }

    useGSAP(() => {
        if (betOptionModalRef.current) {
            gsap.fromTo(
                betOptionModalRef.current,
                {
                    opacity: 0,
                },
                {
                    opacity: 1,
                    duration: 0.25,
                }
            )
        }
    }, [isBetSectionOpened])

    return (
        <div ref={betOptionButtonComponent} className="flex">
            <SideButton
                id="bet-option-button"
                isActive={isActive}
                isInteractive={isInteractive}
                pulse={pulse}
                onClick={onSideButtonClicked}
                texture={!isEQJackpotType ? betOptionButton : betOptionButtonEQ}
                className={cn(
                    buttonConfigStyles,
                    isBetSectionOpened && activeTriggerButtonLabel === 'betSection' ? 'z-[101]' : 'z-[50]'
                )}
                shouldUpdateOpacityWhenInactive={shouldUpdateOpacityWhenInactive}
            />
            {!isLoading && (
                <div
                    ref={betOptionModalRef}
                    className={cn(
                        styles.modalGradientBorder,
                        'fixed flex flex-col justify-center items-center h-auto max-w-full w-max',
                        sectionConfigStyles
                    )}
                    style={{
                        ...sectionConfigPosition,
                        zIndex: isBetSectionOpened ? 101 : 'auto',
                        display: isBetSectionOpened ? 'flex' : 'none',
                    }}
                >
                    <div className="flex w-full my-[12px] items-center">
                        <div className="w-[58px] h-[58px]" />
                        <div className="grow text-white uppercase text-[30px] font-bold text-center">
                            {t('gameui.betOptions')}
                        </div>
                        <button onClick={onSideButtonClicked} className="w-[58px] h-[58px]">
                            <img
                                src={closeButton}
                                alt="Close Bet Options Button"
                                className="w-full h-full object-contain"
                            />
                        </button>
                    </div>
                    <BetOptions shouldCloseOnBetChosen={true} />
                </div>
            )}
        </div>
    )
}

export default BetOptionButton
